import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, AutocompleteRenderOptionState, Box, createFilterOptions, TextFieldProps, Tooltip} from "@mui/material";
import {CancelToken} from "axios";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteValue} from "@mui/material/useAutocomplete/useAutocomplete";
import LazyImage from "../../../app-specific/lazy-image";
import LazySvg from "../../../app-specific/lazy-svg";
import ToastService from "../../../../../core/services/toast";
import CreateNewTagsDialog from "../../../dialogs/create-new-tags";
import UIUtils from "../../../../utils";
import useIndustry from "../../../../hooks/use-industry";

export interface TagOption {
    id: number;
    name: string;
    iconFileName?: string;
    industryId?: string;
}

interface TagAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<TagOption, M, false, false>, 'renderInput' | 'options' | 'renderOption' | 'filterOptions'> {
    industryId?: string;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    selectedTags?: TagOption[];
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: TagOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: TagOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

interface TagSearchResponse {
    data?: TagOption[];
    configuration?: {
        TagIconUrlBaseAddress?: string;
    }
}

interface AddNewTagsDialogState {
    open: boolean;
    initialValues?: {
        keyword?: string;
        industryId?: string;
    }
    close: () => void;
    onSuccess?: (createdTag: TagOption) => void;
}

const initAddNewTagsDialog: AddNewTagsDialogState = {open: false, close: () => void 0};

const TagAutocomplete = <M extends boolean = false>({
                                                        industryId = undefined,
                                                        textFieldProps = {},
                                                        form = false,
                                                        name = undefined,
                                                        required = false,
                                                        setLoadingOptions: setLoadingOptionsProps,
                                                        selectedTags,
                                                        renderOption: customRenderOption,
                                                        ...props
                                                    }: TagAutocompleteProps<M>) => {
    const {industries} = useIndustry({noCategories: true});
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [creating, setCreating] = useState(false);
    const [addNewTagsDialog, setAddNewTagsDialog] = useState<AddNewTagsDialogState>(initAddNewTagsDialog);

    const freeSolo = React.useMemo(() => (props.freeSolo ?? true), [props.freeSolo]);


    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Tag options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (matches against Tag numbers)
     * @returns Array of matching Tag options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = (await Api.getTagsAutocomplete(keywords, industryId, cancelToken)) as TagSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.map(e => ({
                ...e,
                iconFileName: response?.configuration?.TagIconUrlBaseAddress && e.iconFileName ? `${response.configuration.TagIconUrlBaseAddress}${e.iconFileName}` : undefined,
            })) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [industryId, isMounted]);

    /**
     * Determines if two Tag options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: TagOption | AutocompleteFreeSoloValueMapping<boolean>, value: TagOption | null | AutocompleteFreeSoloValueMapping<boolean>) =>
        typeof option === 'string' || typeof value === 'string'
            ? option === value
            : option?.id === value?.id, []);

    /**
     * Gets the display label for a Tag option (Tag number)
     */
    const getOptionLabel = useCallback((option: TagOption | AutocompleteFreeSoloValueMapping<boolean>) => typeof option === 'string' ? option : option?.name ?? "", []);

    /**
     * Gets the unique key for a Tag option
     */
    const getOptionKey = useCallback((option: TagOption | AutocompleteFreeSoloValueMapping<boolean>) => typeof option === 'string' ? option : option?.id ?? option, []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    const selectedTagIds = useMemo(() => selectedTags?.map(e => e.id) ?? [], [selectedTags]);
    const selectedTagNames = useMemo(() => selectedTags?.map(e => e.name.toLowerCase().trim()) ?? [], [selectedTags]);
    const defaultFilterOptions = useMemo(() => createFilterOptions<TagOption>(), []);

    const filterOptions = useCallback((options, params) => {
        options = options?.filter(e => !selectedTagIds?.includes(e.id))
        const filtered = defaultFilterOptions(options, params);
        const inputValue = params.inputValue.toLowerCase()?.trim() ?? "";
        if (
            freeSolo
            && !!inputValue
            && !loadingOptions
            && !selectedTagNames?.includes(inputValue)
            && !filtered?.find(e => e?.name?.toLowerCase()?.trim() === inputValue)
            && operatorAccessLevels.packages.lookup.manageTags
        ) {
            filtered.push(params.inputValue.trim());
        }
        return filtered;
    }, [defaultFilterOptions, freeSolo, loadingOptions, operatorAccessLevels.packages.lookup.manageTags, selectedTagNames, selectedTagIds]);

    /**
     * Default render function for customer options
     * Displays a two-column layout with name and city
     *
     * @param props - HTML attributes for the list item
     * @param option - Customer option to render
     */
    const defaultRenderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
        option: TagOption
    ) => {
        const {key: _, ...otherProps} = props;
        const isFreeSoloValue = typeof option === 'string';
        const tagIndustryId = option?.industryId ?? industryId;
        return (
            <Box component="li" {...otherProps} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                {
                    isFreeSoloValue &&
                    <p className='autocomplete-free-solo-button'>
                        Create Tag: {option}
                    </p>
                }
                {
                    !isFreeSoloValue &&
                    <div className={'tag-container justify-content-start w-100'}>
                        <div className={'tag-icon-container'}>
                            {
                                !!tagIndustryId && industries.length > 1 &&
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    color: 'primary.main',
                                    marginRight: 1,
                                }}>
                                    {
                                        React.cloneElement(UIUtils.getIndustryIcon('', tagIndustryId, true) ??
                                            <div/>, {style: {width: 20, height: 20, color: 'inherit'}})
                                    }
                                </Box>
                            }
                            {
                                option?.iconFileName && (
                                    option?.iconFileName?.toLowerCase()?.endsWith('.png')
                                        ? <LazyImage
                                            src={option?.iconFileName}
                                            height={30}
                                            width={30}
                                        />
                                        : <LazySvg
                                            src={option?.iconFileName}
                                            height={30}
                                            width={30}
                                        />
                                )
                            }
                        </div>
                        <Tooltip title={option?.name ?? ''} placement={'bottom'} arrow>
                            <p className={'mr-4 pr-2'}>
                                {option?.name ?? ''}
                            </p>
                        </Tooltip>
                    </div>
                }
            </Box>
        );
    }, [industries.length, industryId]);

    /**
     * Render function that combines custom and default option rendering
     * Allows for customization while maintaining default layout as fallback
     */
    const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: TagOption, state: AutocompleteRenderOptionState) => {
        if (customRenderOption) {
            return customRenderOption(props, option, state, defaultRenderOption);
        }
        return defaultRenderOption(props, option);
    }, [customRenderOption, defaultRenderOption]);

    /**
     * Creates a new tag in the system with the given tag title.
     * @param tagName
     */
    const createNewTag = useCallback(async (tagName) => {
        setCreating(true);
        const forApi = {
            name: tagName,
            industryId: industryId,
        }
        const response = await Api.createNewTag(forApi);
        if (!isMounted())
            return;
        setCreating(false);
        return response.data;
    }, [industryId, isMounted])

    /**
     * Opens the dialog to create a new tag, returning a promise that resolves with the created tag or undefined if cancelled
     */
    const openTagCreationDialog = useCallback(async (tagName: string) => {
        return new Promise<TagOption | undefined>((resolve) => {
            setAddNewTagsDialog({
                open: true,
                initialValues: {
                    keyword: tagName,
                },
                onSuccess: (createdTag) => resolve(createdTag),
                close: () => {
                    resolve(undefined);
                    setAddNewTagsDialog(initAddNewTagsDialog);
                }
            });
        });
    }, []);

    /**
     * Handles changes to the selected tags, including free solo input
     */
    const onTagsChanged = useCallback(async (event: React.SyntheticEvent, value: AutocompleteValue<TagOption, M, false, boolean>, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<TagOption>,) => {
        const foundFreeSolo = freeSolo ? Array.isArray(value) ? value.find(v => typeof v === 'string') : typeof value === 'string' ? value : undefined : undefined;
        let createdTag;
        if (foundFreeSolo) {
            if (!operatorAccessLevels.packages.lookup.manageTags) {
                ToastService.warning({
                    title: "Insufficient Permissions",
                    message: "You don't have permission to create new tags.",
                });
            } else {
                if (!industryId) {
                    createdTag = await openTagCreationDialog(foundFreeSolo);
                } else {
                    createdTag = await createNewTag(foundFreeSolo)
                }
                if (!createdTag) {
                    ToastService.warning({
                        title: "Tag Not Created",
                        message: "The tag was not created. Please try again.",
                    });
                }
            }
        }
        if (props.multiple) {
            const newValue =
                Array.isArray(value)
                    ? value.map(v => {
                        if (typeof v === 'string')
                            return v === foundFreeSolo && createdTag ? createdTag : null;
                        return v;
                    }).filter(v => v !== null)
                    : typeof value === 'string'
                        ? (value === foundFreeSolo && createdTag ? [createdTag] : [])
                        : !!value
                            ? [value]
                            : [];
            props.onChange?.(event, newValue as any, reason, details);
        } else {
            if (foundFreeSolo && value === foundFreeSolo && createdTag) {
                props.onChange?.(event, createdTag, reason, details);
            } else if (!foundFreeSolo) {
                props.onChange?.(event, value as any, reason, details);
            }
        }
    }, [createNewTag, freeSolo, industryId, openTagCreationDialog, operatorAccessLevels.packages.lookup.manageTags, props]);

    return (
        <>
            <AutocompleteBase<TagOption, M, false, false>
                keywordsBased
                loading={loadingOptions || creating}
                getOptions={getOptions}
                timeout={1000}
                textFieldProps={{
                    InputProps: {
                        startAdornment: (
                            industryId && industries.length > 1 &&
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                color: 'primary.main',
                                marginRight: 1,
                            }}>
                                {React.cloneElement(UIUtils.getIndustryIcon('', industryId, true) ?? <div/>, {style: {width: 20, height: 20, color: 'inherit'}})}
                            </Box>
                        ),
                        ...textFieldProps.InputProps,
                    },
                    ...textFieldProps,
                }}
                form={form}
                name={name}
                required={required}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionLabel={getOptionLabel as any}
                getOptionKey={getOptionKey as any}
                options={defaultOptions}
                filterOptions={filterOptions}
                clearOnBlur={false}
                {...props}
                onChange={onTagsChanged}
                renderOption={renderOption}
            />
            <CreateNewTagsDialog
                onSuccess={addNewTagsDialog.onSuccess}
                open={addNewTagsDialog.open}
                close={addNewTagsDialog.close}
                limitToOne
                initialValues={addNewTagsDialog.initialValues}
            />
        </>
    );
};

export default TagAutocomplete;